import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ToastContext = createContext(null);

let nextToastId = 0;
const DEFAULT_DURATION_MS = 4000;
const DEFAULT_ERROR_DURATION_MS = 6000;
const DEFAULT_ACTION_DURATION_MS = 8000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback(id => {
    setToasts(prev => prev.filter(t => t.id !== id));
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
  }, []);

  const show = useCallback((message, { type = 'success', action, duration } = {}) => {
    const id = ++nextToastId;
    const resolvedDuration =
      duration ?? (action ? DEFAULT_ACTION_DURATION_MS : type === 'error' ? DEFAULT_ERROR_DURATION_MS : DEFAULT_DURATION_MS);
    setToasts(prev => [...prev, { id, message, type, action }]);
    if (resolvedDuration) {
      timersRef.current[id] = setTimeout(() => dismiss(id), resolvedDuration);
    }
    return id;
  }, [dismiss]);

  const showSuccess = useCallback((message, opts) => show(message, { ...opts, type: 'success' }), [show]);
  const showError = useCallback((message, opts) => show(message, { ...opts, type: 'error' }), [show]);

  return (
    <ToastContext.Provider value={{ showSuccess, showError, dismiss }}>
      {children}
      <div
        className="fixed inset-0 z-[10010] flex flex-col items-center justify-center gap-2 px-4 pointer-events-none"
        aria-live="polite"
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }) {
  const isError = toast.type === 'error';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-start gap-2 bg-white rounded-2xl shadow-xl border px-4 py-3 w-full max-w-sm ${
        isError ? 'border-secondary-red' : 'border-slate-200'
      }`}
    >
      {isError ? (
        <ExclamationCircleIcon className="h-5 w-5 text-secondary-red shrink-0 mt-0.5" aria-hidden="true" />
      ) : (
        <CheckCircleIcon className="h-5 w-5 text-slate-700 shrink-0 mt-0.5" aria-hidden="true" />
      )}
      <p className={`text-sm flex-1 ${isError ? 'text-secondary-red' : 'text-slate-700'}`}>{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          className="text-sm font-medium text-secondary-red underline shrink-0 focus:outline-none"
          onClick={() => {
            toast.action.onClick();
            onDismiss();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-slate-300 hover:text-slate-500 transition-colors shrink-0 focus:outline-none"
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
